import { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    MenuItem,
    Paper,
    Select as MuiSelect,
    Stack,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from '@mui/material';
import Select from 'domains/core/components/Select';
import InputLabel from 'domains/core/components/InputLabel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { SQLType, URLPaths } from 'models/enums';
import useContactColumns from 'hooks/queries/useContactColumns';
import useDynamicContentCategories from 'hooks/queries/useDynamicContentCategories';
import useCreateDisplayCondition from 'hooks/mutations/useCreateDisplayCondition';
import useParseMigrateTemplate, {
    ConditionExpressionItem,
    MigrationSource,
} from 'hooks/mutations/useParseMigrateTemplate';
import useExecuteMigrateTemplate, {
    ConditionMappingItem,
    ConversionMode,
} from 'hooks/mutations/useExecuteMigrateTemplate';
import useSaveImportMappings from 'hooks/mutations/useSaveImportMappings';
import useRowDisplayConditions, { RowDisplayCondition } from 'hooks/queries/useDisplayConditions';
import { getOperatorValues } from 'domains/segments/pages/SegmentCreatePage/Filter/utils';
import { getInclusionOperatorFromUIToAPI } from 'domains/segments/utils';

const STEPS = ['Upload HTML', 'Map Personalization Tags', 'Map Conditions', 'Template Details', 'Review & Import'];

type ConditionForm = {
    name: string;
    category: string;
    field: string;
    operator: string;
    value: string;
};

const defaultConditionForm = (): ConditionForm => ({
    name: '',
    category: '',
    field: '',
    operator: '',
    value: '',
});

const numericSqlTypes: SQLType[] = [SQLType.INTEGER, SQLType.DOUBLE_PRECISION];

const SOURCE_LABELS: Record<MigrationSource, string> = { salesforce: 'Salesforce Marketing Cloud' };

/**
 * Score existing conditions against an AMPscript expression and return the
 * best match above the confidence threshold, or null if nothing fits.
 *
 * For ELSE blocks we tokenize the preceding IF expressions so the suggestion
 * points toward the negation field/value the user should set up.
 */
/**
 * Suggests an existing display condition for the given expression.
 *
 * Strategy: extract the SFMC field name from the expression, map it to the
 * Cured field via conditionFieldMapping, then find conditions whose label or
 * description contains that exact Cured field name. This avoids the false
 * positives that arise from fuzzy token matching on expression values.
 *
 * Returns null for ELSE blocks (the negation can't be inferred reliably) and
 * when the field has not yet been mapped by the user.
 */
const getSuggestedCondition = (
    expr: ConditionExpressionItem,
    existingConditions: RowDisplayCondition[],
    conditionFieldMapping: Record<string, string>
): RowDisplayCondition | null => {
    if (!existingConditions.length || expr.isElse) return null;

    const fieldMatch = expr.expression?.match(/\[(\w+)\]/);
    if (!fieldMatch) return null;

    const curedField = conditionFieldMapping[fieldMatch[1]];
    if (!curedField) return null;

    // Match by exact Cured field name (underscored or with spaces)
    const fieldLower = curedField.toLowerCase();
    const fieldSpaced = fieldLower.replace(/_/g, ' ');

    const matches = existingConditions.filter((cond) => {
        const haystack = `${cond.label} ${cond.description}`.toLowerCase();
        return haystack.includes(fieldLower) || haystack.includes(fieldSpaced);
    });

    return matches[0] ?? null;
};

const ImportTemplatePage = () => {
    const history = useHistory();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [activeStep, setActiveStep] = useState(0);
    const [htmlContent, setHtmlContent] = useState('');
    const [fileName, setFileName] = useState('');
    const [parseError, setParseError] = useState('');

    // Step 1: Import configuration
    const [source, setSource] = useState<MigrationSource>('salesforce');
    const [conversionMode, setConversionMode] = useState<ConversionMode>('native');

    // Step 2: AMPscript %%tag%% → Cured merge tag column
    const [tagMapping, setTagMapping] = useState<Record<string, string>>({});
    // Step 2: AMPscript [FieldName] → Cured segmentable column (used in conditions)
    const [conditionFieldMapping, setConditionFieldMapping] = useState<Record<string, string>>({});

    // Step 3: expressionId → created condition mapping
    const [conditionMapping, setConditionMapping] = useState<Record<string, ConditionMappingItem>>({});
    const [conditionForms, setConditionForms] = useState<Record<string, ConditionForm>>({});
    const [creatingForId, setCreatingForId] = useState<string | null>(null);

    // Step 4
    const [templateName, setTemplateName] = useState('');
    const [subjectLine, setSubjectLine] = useState('');
    const [preHeader, setPreHeader] = useState('');
    const [executeError, setExecuteError] = useState('');

    const { data: contactColumnsData } = useContactColumns();
    const mergeTagColumns = (contactColumnsData ?? []).filter((col) => col.isMergeTag);
    const segmentableColumns = (contactColumnsData ?? []).filter((col) => col.isSegmentable);

    const { data: categoriesData } = useDynamicContentCategories();
    const categoryOptions: string[] = categoriesData ?? [];

    const { data: existingConditions = [] } = useRowDisplayConditions();

    const { mutate: createDisplayCondition, isLoading: isCreatingCondition } = useCreateDisplayCondition({
        onSuccess: (newCondition) => {
            if (!creatingForId) return;
            setConditionMapping((prev) => ({
                ...prev,
                [creatingForId]: {
                    expressionId: creatingForId,
                    conditionId: newCondition.id,
                    label: newCondition.label,
                    category: newCondition.type,
                },
            }));
            setCreatingForId(null);
        },
    });

    const { mutate: parseMigrateTemplate, isLoading: isParsing, data: parseResult } = useParseMigrateTemplate({
        onSuccess: (data) => {
            // Auto-populate from saved mappings — only fill fields the user hasn't touched yet
            if (data.savedTagMappings) {
                setTagMapping((prev) => ({ ...data.savedTagMappings, ...prev }));
            }
            if (data.savedConditionFieldMappings) {
                setConditionFieldMapping((prev) => ({ ...data.savedConditionFieldMappings, ...prev }));
            }
        },
    });

    const { mutate: saveImportMappings } = useSaveImportMappings();

    const { mutate: executeMigrateTemplate, isLoading: isExecuting } = useExecuteMigrateTemplate({
        onSuccess: () => {
            history.push(URLPaths.CONTENT);
        },
        onError: (err) => {
            setExecuteError(err.message ?? 'Something went wrong. Please try again.');
        },
    });

    const triggerParse = (content: string, parseSource: MigrationSource) => {
        setParseError('');
        parseMigrateTemplate(
            { htmlContent: content, source: parseSource },
            { onError: (err) => setParseError(err.message ?? 'Failed to parse HTML.') }
        );
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        setParseError('');
        const reader = new FileReader();
        reader.onload = (ev) => {
            const content = ev.target?.result as string;
            setHtmlContent(content);
            triggerParse(content, source);
        };
        reader.readAsText(file);
    };

    const handleSourceChange = (newSource: MigrationSource) => {
        setSource(newSource);
        if (htmlContent) triggerParse(htmlContent, newSource);
    };

    const conditionExpressions: ConditionExpressionItem[] = parseResult?.conditionExpressions ?? [];
    const personalizationTags: string[] = parseResult?.personalizationTags ?? [];
    const conditionFields: string[] = parseResult?.conditionFields ?? [];

    const allTagsMapped = personalizationTags.every((tag) => !!tagMapping[tag]);
    const allConditionFieldsMapped = conditionFields.every((f) => !!conditionFieldMapping[f]);
    const allConditionsMapped = conditionExpressions.every((expr) => !!conditionMapping[expr.id]);

    const getConditionForm = (id: string, expr?: ConditionExpressionItem): ConditionForm => {
        const stored = conditionForms[id];
        if (stored && Object.values(stored).some((v) => v !== '')) return stored;

        // Pre-fill field + value from the parsed expression and the user's field mapping
        if (expr && !expr.isElse && expr.expression) {
            const fieldMatch = expr.expression.match(/\[(\w+)\]/);
            const valueMatch = expr.expression.match(/"([^"]+)"/);
            const sfmcField = fieldMatch?.[1] ?? '';
            const value = valueMatch?.[1] ?? '';
            const field = conditionFieldMapping[sfmcField] ?? '';
            if (field || value) return { ...defaultConditionForm(), field, value };
        }

        return defaultConditionForm();
    };

    const updateConditionForm = (id: string, patch: Partial<ConditionForm>) => {
        setConditionForms((prev) => ({
            ...prev,
            [id]: { ...(prev[id] ?? defaultConditionForm()), ...patch },
        }));
    };

    const handleCreateCondition = (expr: ConditionExpressionItem) => {
        const form = getConditionForm(expr.id);
        const selectedColumn = segmentableColumns.find((c) => c.columnName === form.field);
        const apiOperator = getInclusionOperatorFromUIToAPI(form.operator as any) as string;
        const isNumeric = selectedColumn && numericSqlTypes.includes(selectedColumn.sqlType);
        const value = isNumeric ? (Number(form.value) as any) : form.value;

        setCreatingForId(expr.id);
        createDisplayCondition({
            label: form.name,
            category: form.category,
            description: `${form.field} ${form.operator} ${form.value}`,
            condition: {
                matchType: 'AND',
                demographic: [{ name: form.field, operator: apiOperator as any, value }],
                engagement: [],
                contactEvent: [],
            },
        });
    };

    const handleNext = () => {
        // Persist tag + condition field mappings when leaving step 2
        if (activeStep === 1) {
            saveImportMappings({ source, tagMapping, conditionFieldMapping });
        }
        setActiveStep((s) => s + 1);
    };
    const handleBack = () => setActiveStep((s) => s - 1);

    const handleExecute = () => {
        setExecuteError('');
        executeMigrateTemplate({
            htmlContent,
            templateName,
            subjectLine: subjectLine || undefined,
            preHeader: preHeader || undefined,
            source,
            conversionMode,
            tagMapping,
            conditionFieldMapping,
            conditionMapping: Object.values(conditionMapping),
        });
    };

    const renderStep1 = () => (
        <Stack spacing={3} alignItems="flex-start">
            <Typography variant="body1" color="text.secondary">
                Select the source platform and conversion strategy, then upload your HTML file.
            </Typography>

            <Select
                label={
                    <InputLabel
                        label="Source Platform"
                        labelIconTooltipText="The platform your HTML template was created in. This determines how personalization tags and conditions are parsed."
                    />
                }
                value={source}
                onChange={(e) => handleSourceChange(e.target.value as MigrationSource)}
                values={[{ label: 'Salesforce Marketing Cloud', value: 'salesforce' }]}
                sx={{ width: 320 }}
                data-testid="source-select"
            />

            <Select
                label={
                    <InputLabel
                        label="Conversion Strategy"
                        labelIconTooltipText={
                            <>
                                Native BEE JSON parses blocks into editable modules (images, text, buttons, dividers).
                                <br />
                                Import as HTML embeds each row as a raw HTML block - preserves original markup but is
                                less editable in the template builder.
                            </>
                        }
                    />
                }
                value={conversionMode}
                onChange={(e) => setConversionMode(e.target.value as ConversionMode)}
                values={[
                    { label: 'Convert to native BEE JSON', value: 'native' },
                    { label: 'Import as HTML', value: 'html' },
                ]}
                sx={{ width: 320 }}
                data-testid="conversion-mode-select"
            />

            <Button
                variant="outlined"
                endIcon={<FileUploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={isParsing}
                data-testid="choose-file-button"
            >
                {fileName ? 'Replace file' : 'Choose HTML file'}
            </Button>
            <input ref={fileInputRef} type="file" accept=".html,.htm" hidden onChange={handleFileChange} />
            {fileName && (
                <Typography variant="body2" color="text.secondary">
                    Selected: <strong>{fileName}</strong>
                </Typography>
            )}
            {isParsing && (
                <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} role="progressbar" aria-label="Parsing HTML" />
                    <Typography variant="body2">Parsing HTML...</Typography>
                </Stack>
            )}
            {parseError && <Alert severity="error">{parseError}</Alert>}
            {parseResult && !isParsing && (
                <Alert severity="success">
                    Found {personalizationTags.length} personalization tag
                    {personalizationTags.length !== 1 ? 's' : ''} and {conditionExpressions.length} condition expression
                    {conditionExpressions.length !== 1 ? 's' : ''}.
                </Alert>
            )}
        </Stack>
    );

    const renderStep2 = () => (
        <Stack spacing={3}>
            <Typography variant="body1" color="text.secondary">
                Map SFMC field names to their Cured equivalents. Personalization tags are used for merge content;
                condition fields drive display logic.
            </Typography>

            {personalizationTags.length === 0 && conditionFields.length === 0 && (
                <Alert severity="info">No fields to map in this template.</Alert>
            )}

            {personalizationTags.length > 0 && (
                <Stack spacing={2}>
                    <Typography variant="subtitle2">Personalization Tags</Typography>
                    {personalizationTags.map((tag) => (
                        <Stack key={tag} direction="row" spacing={2} alignItems="center">
                            <Box sx={{ minWidth: 200 }}>
                                <Chip label={tag} variant="outlined" sx={{ fontFamily: 'monospace' }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                →
                            </Typography>
                            <Autocomplete
                                sx={{ width: 280 }}
                                options={mergeTagColumns}
                                getOptionLabel={(opt) => opt.columnName}
                                value={mergeTagColumns.find((c) => c.columnName === tagMapping[tag]) ?? null}
                                onChange={(_, newValue) => {
                                    setTagMapping((prev) => ({
                                        ...prev,
                                        [tag]: newValue?.columnName ?? '',
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Cured field"
                                        size="small"
                                        placeholder="Select field..."
                                    />
                                )}
                            />
                        </Stack>
                    ))}
                </Stack>
            )}

            {conditionFields.length > 0 && (
                <Stack spacing={2}>
                    {personalizationTags.length > 0 && <Divider />}
                    <Stack spacing={0.5}>
                        <Typography variant="subtitle2">Condition Fields</Typography>
                        <Typography variant="body2" color="text.secondary">
                            These fields appear in IF/ELSE expressions. Mapping them lets Cured pre-fill the condition
                            builder and improve suggestions.
                        </Typography>
                    </Stack>
                    {conditionFields.map((field) => (
                        <Stack key={field} direction="row" spacing={2} alignItems="center">
                            <Box sx={{ minWidth: 200 }}>
                                <Chip label={`[${field}]`} variant="outlined" sx={{ fontFamily: 'monospace' }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                →
                            </Typography>
                            <Autocomplete
                                sx={{ width: 280 }}
                                options={segmentableColumns}
                                getOptionLabel={(opt) => opt.columnName}
                                value={
                                    segmentableColumns.find((c) => c.columnName === conditionFieldMapping[field]) ??
                                    null
                                }
                                onChange={(_, newValue) => {
                                    setConditionFieldMapping((prev) => ({
                                        ...prev,
                                        [field]: newValue?.columnName ?? '',
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Cured field"
                                        size="small"
                                        placeholder="Select field..."
                                    />
                                )}
                            />
                        </Stack>
                    ))}
                </Stack>
            )}
        </Stack>
    );

    const renderConditionExpressionCard = (expr: ConditionExpressionItem) => {
        const mapped = conditionMapping[expr.id];
        const form = getConditionForm(expr.id, expr);
        const isCreatingThis = creatingForId === expr.id && isCreatingCondition;
        const suggested = !mapped ? getSuggestedCondition(expr, existingConditions, conditionFieldMapping) : null;

        const selectedColumn = segmentableColumns.find((c) => c.columnName === form.field);
        const operatorOptions = selectedColumn ? getOperatorValues(selectedColumn.sqlType) : [];

        const isFormComplete =
            form.name.trim().length >= 3 &&
            form.category.trim().length > 0 &&
            form.field.trim().length > 0 &&
            form.operator.trim().length > 0 &&
            form.value.trim().length > 0;

        return (
            <Paper key={expr.id} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                            {expr.id}
                        </Typography>
                        {expr.isElse && <Chip label="ELSE" size="small" />}
                        {mapped && <CheckCircleOutlineIcon color="success" fontSize="small" />}
                    </Stack>

                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {expr.humanReadable}
                    </Typography>

                    {mapped ? (
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Typography variant="body2" color="success.main">
                                Mapped to: <strong>{mapped.label}</strong> ({mapped.category})
                            </Typography>
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                    setConditionMapping((prev) => {
                                        const next = { ...prev };
                                        delete next[expr.id];
                                        return next;
                                    });
                                }}
                            >
                                Remap
                            </Button>
                        </Stack>
                    ) : (
                        <>
                            {expr.isElse && (
                                <Alert severity="warning" sx={{ py: 0.5 }}>
                                    This ELSE block must be mapped to a condition — typically the negation of the
                                    preceding IF (e.g. if IF is &ldquo;market = COASTAL&rdquo;, create &ldquo;market ≠
                                    COASTAL&rdquo;). Without a condition, both blocks will render simultaneously.
                                </Alert>
                            )}
                            {suggested && (
                                <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'info.main' }}>
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Stack spacing={0.25}>
                                            <Typography variant="caption" color="info.main" fontWeight={600}>
                                                Suggested match
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>{suggested.label}</strong>
                                            </Typography>
                                            {suggested.description && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {suggested.description}
                                                </Typography>
                                            )}
                                        </Stack>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() =>
                                                setConditionMapping((prev) => ({
                                                    ...prev,
                                                    [expr.id]: {
                                                        expressionId: expr.id,
                                                        conditionId: suggested.id,
                                                        label: suggested.label,
                                                        category: suggested.type,
                                                    },
                                                }))
                                            }
                                        >
                                            Use this
                                        </Button>
                                    </Stack>
                                </Paper>
                            )}
                            <Autocomplete
                                options={existingConditions}
                                getOptionLabel={(opt) => opt.label}
                                renderOption={(props, opt) => (
                                    <li {...props}>
                                        <Stack>
                                            <Typography variant="body2">{opt.label}</Typography>
                                            {opt.description && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {opt.description}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </li>
                                )}
                                onChange={(_, selected) => {
                                    if (!selected) return;
                                    setConditionMapping((prev) => ({
                                        ...prev,
                                        [expr.id]: {
                                            expressionId: expr.id,
                                            conditionId: selected.id,
                                            label: selected.label,
                                            category: selected.type,
                                        },
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select existing condition"
                                        size="small"
                                        placeholder="Search by name..."
                                    />
                                )}
                                sx={{ maxWidth: 400 }}
                            />
                            <Divider>
                                <Typography variant="caption" color="text.secondary">
                                    or create a new one
                                </Typography>
                            </Divider>
                            <Stack direction="row" spacing={2} flexWrap="wrap">
                                <TextField
                                    label="Condition name"
                                    size="small"
                                    sx={{ width: 220 }}
                                    value={form.name}
                                    onChange={(e) => updateConditionForm(expr.id, { name: e.target.value })}
                                    inputProps={{ maxLength: 50 }}
                                />
                                <Autocomplete
                                    sx={{ width: 200 }}
                                    freeSolo
                                    options={categoryOptions}
                                    value={form.category}
                                    onInputChange={(_, val) => updateConditionForm(expr.id, { category: val })}
                                    renderInput={(params) => <TextField {...params} label="Category" size="small" />}
                                />
                            </Stack>
                            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                <Autocomplete
                                    sx={{ width: 220 }}
                                    options={segmentableColumns}
                                    getOptionLabel={(opt) => opt.columnName}
                                    value={segmentableColumns.find((c) => c.columnName === form.field) ?? null}
                                    onChange={(_, newValue) => {
                                        updateConditionForm(expr.id, {
                                            field: newValue?.columnName ?? '',
                                            operator: '',
                                        });
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Cured field" size="small" />}
                                />
                                <MuiSelect
                                    size="small"
                                    displayEmpty
                                    sx={{ width: 200 }}
                                    value={form.operator}
                                    disabled={!form.field}
                                    onChange={(e) => updateConditionForm(expr.id, { operator: e.target.value })}
                                    renderValue={(val) => val || <span style={{ color: '#aaa' }}>Operator</span>}
                                >
                                    {operatorOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </MuiSelect>
                                <TextField
                                    label="Value"
                                    size="small"
                                    sx={{ width: 160 }}
                                    value={form.value}
                                    disabled={!form.operator}
                                    onChange={(e) => updateConditionForm(expr.id, { value: e.target.value })}
                                    type={
                                        selectedColumn && numericSqlTypes.includes(selectedColumn.sqlType)
                                            ? 'number'
                                            : 'text'
                                    }
                                />
                            </Stack>
                            <Box>
                                <Button
                                    variant="contained"
                                    size="small"
                                    disabled={!isFormComplete || isCreatingThis}
                                    onClick={() => handleCreateCondition(expr)}
                                >
                                    {isCreatingThis ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        'Create Condition'
                                    )}
                                </Button>
                            </Box>
                        </>
                    )}
                </Stack>
            </Paper>
        );
    };

    const renderStep3 = () => (
        <Stack spacing={3}>
            <Typography variant="body1" color="text.secondary">
                For each condition expression, select an existing display condition or create a new one. All expressions
                — including ELSE blocks — must be mapped so only one block renders at a time.
            </Typography>
            {conditionExpressions.length === 0 && (
                <Alert severity="info">No display conditions found in this template.</Alert>
            )}
            {conditionExpressions.map(renderConditionExpressionCard)}
        </Stack>
    );

    const renderStep4 = () => (
        <Stack spacing={3} sx={{ maxWidth: 480 }}>
            <Typography variant="body1" color="text.secondary">
                Provide the details for the imported template.
            </Typography>
            <TextField
                label="Template name"
                required
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g. SFMC Welcome Email"
            />
            <TextField
                label="Subject line"
                value={subjectLine}
                onChange={(e) => setSubjectLine(e.target.value)}
                placeholder="e.g. Welcome to our program!"
            />
            <TextField
                label="Pre-header"
                value={preHeader}
                onChange={(e) => setPreHeader(e.target.value)}
                placeholder="e.g. Here's what you need to know..."
            />
        </Stack>
    );

    const renderStep5 = () => (
        <Stack spacing={3}>
            <Typography variant="body1" color="text.secondary">
                Review your import settings and click <strong>Import</strong> to create the template draft.
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    Import Configuration
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Stack spacing={0.5}>
                    <Typography variant="body2">
                        <strong>Source:</strong> {SOURCE_LABELS[source]}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Conversion:</strong>{' '}
                        {conversionMode === 'native' ? 'Convert to native BEE JSON' : 'Import as HTML'}
                    </Typography>
                </Stack>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    Template Details
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Stack spacing={0.5}>
                    <Typography variant="body2">
                        <strong>Name:</strong> {templateName}
                    </Typography>
                    {subjectLine && (
                        <Typography variant="body2">
                            <strong>Subject:</strong> {subjectLine}
                        </Typography>
                    )}
                    {preHeader && (
                        <Typography variant="body2">
                            <strong>Pre-header:</strong> {preHeader}
                        </Typography>
                    )}
                </Stack>
            </Paper>
            {personalizationTags.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Personalization Tags ({personalizationTags.length})
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <Stack spacing={0.5}>
                        {personalizationTags.map((tag) => (
                            <Typography key={tag} variant="body2" sx={{ fontFamily: 'monospace' }}>
                                %%{tag}%% → {tagMapping[tag] ? `{{${tagMapping[tag]}}}` : '(unmapped)'}
                            </Typography>
                        ))}
                    </Stack>
                </Paper>
            )}
            {conditionExpressions.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Display Conditions ({conditionExpressions.length})
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <Stack spacing={0.5}>
                        {conditionExpressions.map((expr) => {
                            const mapped = conditionMapping[expr.id];
                            return (
                                <Typography key={expr.id} variant="body2">
                                    {expr.id}: {mapped ? mapped.label : '(unmapped)'}
                                </Typography>
                            );
                        })}
                    </Stack>
                </Paper>
            )}
            {executeError && <Alert severity="error">{executeError}</Alert>}
        </Stack>
    );

    const renderCurrentStep = () => {
        switch (activeStep) {
            case 0:
                return renderStep1();
            case 1:
                return renderStep2();
            case 2:
                return renderStep3();
            case 3:
                return renderStep4();
            case 4:
                return renderStep5();
            default:
                return null;
        }
    };

    const canProceedFromStep = () => {
        switch (activeStep) {
            case 0:
                return !!parseResult && !isParsing && !parseError;
            case 1:
                return (
                    (allTagsMapped || personalizationTags.length === 0) &&
                    (allConditionFieldsMapped || conditionFields.length === 0)
                );
            case 2:
                return allConditionsMapped || conditionExpressions.length === 0;
            case 3:
                return !!templateName.trim();
            default:
                return true;
        }
    };

    return (
        <Box sx={{ p: 4, maxWidth: 900, mx: 'auto' }}>
            <Typography variant="h5" fontWeight={600} mb={4}>
                Import Template
            </Typography>
            <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
                {STEPS.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>
            <Box sx={{ mb: 5 }}>{renderCurrentStep()}</Box>
            <Stack direction="row" spacing={2}>
                {activeStep > 0 && (
                    <Button variant="outlined" onClick={handleBack} disabled={isExecuting}>
                        Back
                    </Button>
                )}
                {activeStep < STEPS.length - 1 ? (
                    <Button variant="contained" onClick={handleNext} disabled={!canProceedFromStep()}>
                        Next
                    </Button>
                ) : (
                    <Button variant="contained" onClick={handleExecute} disabled={isExecuting || !templateName.trim()}>
                        {isExecuting ? <CircularProgress size={20} color="inherit" /> : 'Import Template'}
                    </Button>
                )}
                <Button variant="text" onClick={() => history.push(URLPaths.CONTENT)} disabled={isExecuting}>
                    Cancel
                </Button>
            </Stack>
        </Box>
    );
};

export default ImportTemplatePage;
